import { useMultipleFeatureFlags } from '@agentnet/feature-flags';
import { MarketplaceProduct } from 'api/admin/types';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useEffect, useState } from 'react';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';

export type UnderwriterProductStatus = {
  contactValidation?: boolean;
  enhancedIdentityVerification?: boolean;
  wireValidation?: boolean;
};

export interface UnderwriterProductStatusResult {
  // False when the flag is off, which keeps every caller on the firm level marketplace status.
  isFeatureEnabled: boolean;
  // Null until the first response for the selected underwriter arrives.
  productStatus: UnderwriterProductStatus | null;
  isLoading: boolean;
}

const productNameMap: { [key: string]: keyof UnderwriterProductStatus } = {
  'Contact Validation': 'contactValidation',
  'Identity Verification': 'enhancedIdentityVerification',
  'Wire Validation': 'wireValidation',
};

export function useUnderwriterProductStatus(
  underwriterCode: string | undefined,
  isDrawerOpen: boolean,
): UnderwriterProductStatusResult {
  const [nonFaUw] = useMultipleFeatureFlags(FeatureFlag.NON_FA_UW_ANS_679_ENABLED);
  const { fetchUnderwriterProducts, fetchUnderwriterProductsResults, fetchUnderwriterProductsStatus } =
    useMarketPlace();
  const [productStatus, setProductStatus] = useState<UnderwriterProductStatus | null>(null);

  const isFeatureEnabled = nonFaUw.ready && !!nonFaUw.value;
  const shouldFetch = isFeatureEnabled && isDrawerOpen && !!underwriterCode;

  // fetchUnderwriterProducts comes from useAsync, and the underlying function in useMarketPlace
  // is recreated each render, so including it in deps would cause repeated fetches hence excluding
  useEffect(() => {
    if (shouldFetch && underwriterCode) {
      fetchUnderwriterProducts(underwriterCode);
    }
  }, [shouldFetch, underwriterCode]);

  // useAsync clears its value while a request is in flight, so the last known status is held
  // here to keep the products from flickering back to enabled between underwriter changes.
  useEffect(() => {
    if (!fetchUnderwriterProductsResults) {
      return;
    }

    const status: UnderwriterProductStatus = {};
    fetchUnderwriterProductsResults.forEach((product: MarketplaceProduct) => {
      const key = productNameMap[product.productName];
      if (key) {
        status[key] = product.isEnabled;
      }
    });
    setProductStatus(status);
  }, [fetchUnderwriterProductsResults]);

  useEffect(() => {
    if (!underwriterCode) {
      setProductStatus(null);
    }
  }, [underwriterCode]);

  return {
    isFeatureEnabled,
    productStatus,
    isLoading: shouldFetch && fetchUnderwriterProductsStatus === AsyncStatus.PENDING,
  };
}
